import classNames from 'classnames';
import { InputFile, FlexContainer, Button, Txt } from 'components';
import { picturesMaxSize } from 'config';
import { resizeImageFiles } from 'helpers/resizeImageFile';
import { useCallback, useMemo, useRef, useState } from 'react';
import { PictureItem } from './components';
import { DndBackend, DndTarget } from './dnd';
import Icon from '../Icon';
import styles from './InputImages.module.scss';

/**
 * InputImages
 *
 * @return {JSX.Element}
 */
const InputImages = ({
  className,
  value,
  name,
  required,
  disabled,
  onChange,
  multiple,
  resize = picturesMaxSize.property,
  reverse,
}) => {
  const localValue = useMemo(
    () => (multiple ? value || [] : value || ''),
    [value, multiple]
  );
  const [isBusy, setIsBusy] = useState(false);
  const fileInputRef = useRef(null);
  const hiddenValue = (multiple ? localValue.length : localValue) || '';

  const handleImagesAdd = async ({ target: { files } }) => {
    setIsBusy(true);
    try {
      const settledPromises = await resizeImageFiles(files, resize, resize);
      const urls = settledPromises
        .filter(({ status }) => status === 'fulfilled')
        .map((r) => r.value);
      if (urls.length) {
        const newValue = Array.from(
          new Set([...urls, ...(multiple ? localValue : [localValue])])
        );
        const shouldTriggerChange = multiple
          ? newValue.length > localValue.length
          : newValue[0] !== localValue;
        if (shouldTriggerChange) {
          onChange({
            target: { name, value: multiple ? newValue : newValue[0] },
          });
        }
      } else {
        console.warn('No compatible images found');
      }
    } catch (e) {
      console.warn('Failed to read files', e.toString());
    }
    setIsBusy(false);
  };
  const handleHiddenInputFocus = () => fileInputRef.current?.focus();
  const handleRemovePicture = (i) => () => {
    if (multiple) {
      const idx = localValue.indexOf(i);
      const newValue = [...localValue];
      newValue.splice(idx, 1);
      onChange({ target: { name, value: newValue } });
    } else {
      onChange({ target: { name, value: '' } });
    }
  };

  const handleMoveCard = useCallback(
    (from, to) => {
      const newValue = [...localValue];
      newValue.splice(to, 0, newValue.splice(from, 1)[0]);
      onChange({ target: { name, value: newValue } });
    },
    [localValue, onChange, name]
  );

  const isDisabled = disabled || isBusy;

  console.log(multiple, localValue);

  return (
    <FlexContainer
      className={classNames(
        styles.InputImages,
        multiple && styles.InputImages__multiple,
        className,
        `${InputImages.displayName}`
      )}
      column
      gap
    >
      {localValue && localValue.length ? (
        <DndBackend
          onMove={handleMoveCard}
          className={classNames(
            styles.ImagesContainer,
            styles.ImagesContainer__dnd
          )}
        >
          {(Array.isArray(localValue) ? localValue : [localValue]).map(
            (i, idx) => (
              <DndTarget
                key={i}
                index={idx}
                id={i.slice(-100)}
                className={classNames(
                  styles.ImageContainer,
                  styles.ImageContainer__dnd
                )}
                disabled={!multiple || disabled}
              >
                <PictureItem
                  disabled={disabled}
                  src={i}
                  onRemove={handleRemovePicture(i)}
                />
              </DndTarget>
            )
          )}
        </DndBackend>
      ) : null}
      <FlexContainer className={styles.ButtonHolder}>
        <input
          className={styles.HiddenInput}
          name={name}
          value={hiddenValue}
          required={required}
          onChange={() => {}}
          onFocus={handleHiddenInputFocus}
          tabIndex={-1}
        />
        {!multiple && localValue ? null : (
          <InputFile
            ref={fileInputRef}
            flex
            disabled={isDisabled}
            accept="image/jpeg,image/png"
            onChange={handleImagesAdd}
            multiple={multiple}
          >
            <Button
              flex
              className={styles.Button}
              disabled={isDisabled}
              tabIndex={-1}
              big
              reverse={reverse}
            >
              <FlexContainer spaceBetween alignCenter flex>
                <Txt xx>Add image{multiple ? 's' : ''}</Txt>
                <Icon name="plus" />
              </FlexContainer>
            </Button>
          </InputFile>
        )}
      </FlexContainer>
    </FlexContainer>
  );
};

InputImages.displayName = 'InputImages';

export default InputImages;
